import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { EmptyState, LoadingState, useToast } from '@platform/ui-kit'
import { ApiErrorState } from '@/app/errors/ApiErrorState'
import { useAuth } from '@/app/providers/AuthProvider'
import { chatApi } from './api'
import { setChatUnread } from './unreadStore'
import { parseChatNotification, showDesktopNotification } from './chatNotification'
import { CHAT_ATTEMPTS_KEY, registerChatSignOut } from './chatSession'

/* ------------------------------------------------------------------
   گفتگو — Rocket.Chat در قابی که با جابه‌جاییِ صفحه‌ها نمی‌میرد
   ------------------------------------------------------------------
   🔴 چرا اینجا و نه در خودِ صفحهٔ `/app/chat`.

   قاب داخلِ صفحه بود؛ رفتن به صفحهٔ دیگر آن را از DOM برمی‌داشت و
   برگشتن، کلِ Rocket.Chat را از صفر بار می‌کرد — چند ثانیه و یک
   ورودِ دوباره، هر بار. حالا یک بار، پس از اولین بازدید، در قالبِ
   پنل ساخته می‌شود و بیرون از گفتگو فقط پنهان (`hidden`) است.

   ⚠️ قاب هرگز جابه‌جا نمی‌شود — جابه‌جاییِ iframe در DOM همان
   بارگذاریِ دوباره است. فقط نمایانی‌اش عوض می‌شود.

   ------------------------------------------------------------------
   🔴 ورود: Rocket.Chat خودش می‌گوید وارد هست یا نه.

   دو «Custom Script» در Rocket.Chat (Layout → Custom Scripts) به این
   صفحه پیام می‌دهند: `{carbon:'logged-out'}` و `{carbon:'logged-in'}`.
   با `logged-out` ورودِ OAuth شروع می‌شود؛ تا `logged-in` نیاید،
   پوششِ «در حال ورود» رویِ قاب است تا صفحهٔ ورودِ Rocket.Chat
   اصلاً دیده نشود.

   ⚠️ اگر آن اسکریپت‌ها تنظیم نشده باشند، رفتارِ قبلی می‌ماند: یک بار
   در هر زبانه فرمانِ ورود فرستاده می‌شود و پوشش پس از چند ثانیه کنار
   می‌رود. یعنی تنظیم‌نکردنشان چیزی را نمی‌شکند.

   ------------------------------------------------------------------
   🔴 «وارد هست» کافی نیست — باید همان کسی باشد که وارد پنل شده.

   Rocket.Chat جلسه‌اش را جدا از پنل در مرورگر نگه می‌دارد. دیده‌شده
   ۲۰۲۶-۱۰-۰۴: پس از خروجِ یک نفر و ورودِ نفرِ دیگر در همان مرورگر،
   گفتگو هنوز با حسابِ نفرِ اول باز می‌شد. پس `logged-in` ایمیل‌های
   کاربرِ Rocket.Chat را هم می‌آورد؛ اگر ایمیلِ کاربرِ پنل میانشان
   نبود، قاب خارج می‌شود و ورودِ OAuth با کاربرِ درست از نو شروع
   می‌شود. تا این بررسی تمام نشده، پوشش کنار نمی‌رود و عدد و اعلانی
   از قاب پذیرفته نمی‌شود — وگرنه همان لحظه گفتگوی دیگری لو می‌رفت.

   ⚠️ اسکریپتِ قدیمی که ایمیل نمی‌فرستد همان رفتارِ قبلی را دارد.
   ------------------------------------------------------------------ */

const ATTEMPTS_KEY = CHAT_ATTEMPTS_KEY

/** خروجِ قاب منتظرِ تأییدِ Rocket.Chat می‌ماند، ولی نه بیشتر از این. */
const SIGN_OUT_WAIT_MS = 1500

/** سقفِ تلاشِ ورود در یک زبانه — اگر OAuth خطا بدهد، حلقه نمی‌سازد. */
const MAX_ATTEMPTS = 3

/** بی‌خبر از Rocket.Chat، پوشش پس از این مدت کنار می‌رود. */
const OVERLAY_FALLBACK_MS = 8000

/**
 * 🔴 ورودی که این مدت پس از `logged-out` به `logged-in` نرسید، گیر کرده است.
 *
 * ⚠️ دیده‌شده ۲۰۲۶-۱۰-۰۵: فرمانِ ورود رفت ولی OAuth در Rocket.Chat کامل
 * نشد، و پوششِ «در حال ورود» هر خطا یا دکمه‌ای را که Rocket.Chat نشان
 * می‌داد پنهان کرد — کاربر بی‌هیچ راهی گیر افتاد. پس از این مدت پوشش
 * کنار می‌رود و صفحهٔ خودِ Rocket.Chat دیده می‌شود. آن صفحه خارج‌شده
 * است، پس چیزی از کاربرِ دیگری نشان نمی‌دهد.
 */
const LOGIN_STALL_MS = 15000

/** بی‌خبر از `startup` ِ Rocket.Chat، فرمانِ ورود پس از این مدت به‌هرحال می‌رود. */
const READY_FALLBACK_MS = 4000

/** فرمانِ ورودی که تا این مدت به ورود نرسید، یک بارِ دیگر فرستاده می‌شود. */
const LOGIN_RETRY_MS = 6000

function attempts(): number {
  try {
    return Number(sessionStorage.getItem(ATTEMPTS_KEY) ?? '0') || 0
  } catch {
    return 0
  }
}

function countAttempt(): void {
  try {
    sessionStorage.setItem(ATTEMPTS_KEY, String(attempts() + 1))
  } catch {
    /* ذخیره‌سازیِ بسته — سقف در همین بارگذاری هم با شمارندهٔ حافظه نگه داشته می‌شود. */
  }
}

function resetAttempts(): void {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY)
  } catch {
    /* بی‌اثر */
  }
}

/**
 * ⚠️ قاب پس از این مدت در پس‌زمینه هم ساخته می‌شود — نه فقط با بازدیدِ
 * گفتگو. عددِ خوانده‌نشده‌های منو را خودِ Rocket.Chat از داخلِ قاب
 * می‌فرستد؛ بی قاب، منو تا اولین بازدیدِ گفتگو همیشه صفر نشان می‌داد.
 * تأخیر برای این است که بارگذاریِ چت با بارگذاریِ خودِ صفحه رقابت نکند.
 */
const BACKGROUND_DELAY_MS = 3000

const chatConfigQuery = {
  queryKey: ['me', 'chat'],
  queryFn: chatApi.config,
  staleTime: Infinity,
} as const

export function ChatFrameHost() {
  const location = useLocation()
  const onChat = location.pathname === '/app/chat' || location.pathname.startsWith('/app/chat/')

  const config = useQuery(chatConfigQuery)

  /*
    🔴 پس‌زمینه فقط وقتی ورودِ خودکار تنظیم شده است — قابی که در
    پس‌زمینه پشتِ صفحهٔ ورود بماند هیچ عددی نمی‌فرستد و فقط بار است.
  */
  const canPreload = Boolean(config.data?.url && config.data?.sso_service)

  /* قاب یک بار ساخته می‌شود و تا خروج از پنل می‌ماند. */
  const [activated, setActivated] = useState(onChat)
  useEffect(() => {
    if (onChat) setActivated(true)
  }, [onChat])

  useEffect(() => {
    if (activated || !canPreload) return
    const timer = window.setTimeout(() => setActivated(true), BACKGROUND_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [activated, canPreload])

  if (!activated) return null

  return <ChatFrame visible={onChat} />
}

function ChatFrame({ visible }: { visible: boolean }) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [signedIn, setSignedIn] = useState(false)
  const [mismatch, setMismatch] = useState(false)
  const [loginStalled, setLoginStalled] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()
  const { user } = useAuth()

  /* اثرِ گوش‌دادن فقط با عوض‌شدنِ سرور از نو ساخته می‌شود؛ نمایانی و
     ابزارها از راهِ ref خوانده می‌شوند تا شنونده کهنه نماند. */
  const panelEmail = user?.email?.trim().toLowerCase() || null
  const live = useRef({ visible, navigate, toast, panelEmail })
  live.current = { visible, navigate, toast, panelEmail }

  const query = useQuery(chatConfigQuery)

  const url = query.data?.url ?? null
  const service = query.data?.sso_service ?? null

  useEffect(() => {
    if (!url) return

    const origin = new URL(url).origin
    let heardFromScripts = false
    let lastStart = 0
    let localAttempts = 0
    /** کاربرِ داخلِ قاب همان کاربرِ پنل است — تا آن وقت عدد و اعلان پذیرفته نمی‌شود. */
    let verified = false
    let mismatches = 0
    let signedOut: (() => void) | null = null
    /*
      🔴 خروج از پنل در جریان است — ورودِ دوباره شروع نمی‌شود.

      ⚠️ اگر قاب پیش از برداشته‌شدن هنوز `logged-out` بفرستد و اینجا ورود
      شروع شود، OAuth با نشستِ هنوز زندهٔ پنل همان کاربرِ در حالِ خروج
      را دوباره وارد می‌کند.
    */
    let leaving = false
    let stallTimer: number | undefined

    const clearStall = () => {
      if (stallTimer !== undefined) window.clearTimeout(stallTimer)
      stallTimer = undefined
      setLoginStalled(false)
    }

    const armStall = () => {
      clearStall()
      stallTimer = window.setTimeout(() => setLoginStalled(true), LOGIN_STALL_MS)
    }

    const requestSignOut = () => {
      frameRef.current?.contentWindow?.postMessage({ carbonCommand: 'logout' }, origin)
    }

    const unregister = registerChatSignOut(
      () =>
        new Promise<void>((resolve) => {
          if (!heardFromScripts) {
            resolve()
            return
          }
          leaving = true
          const timer = window.setTimeout(resolve, SIGN_OUT_WAIT_MS)
          signedOut = () => {
            window.clearTimeout(timer)
            resolve()
          }
          requestSignOut()
        }),
    )

    /*
      🔴 پوستهٔ پنل به قاب — Rocket.Chat روشن/تاریک را از پنل می‌گیرد.
      اسکریپت‌های سفارشیِ Rocket.Chat آن را روی `data-carbon-theme`
      می‌گذارند (infra/rocketchat/). پس از هر پیامِ ورود/خروج دوباره
      فرستاده می‌شود، چون آن پیام یعنی اسکریپت گوش می‌دهد.
    */
    const postTheme = () => {
      frameRef.current?.contentWindow?.postMessage(
        { carbonTheme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light' },
        origin,
      )
    }

    const themeObserver = new MutationObserver(postTheme)
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    const startLogin = () => {
      if (!service) return

      /* ⚠️ دو فرمانِ پشتِ سرِ هم (startup و load) یک ورود حساب می‌شوند. */
      const now = Date.now()
      if (now - lastStart < 15000) return
      if (attempts() >= MAX_ATTEMPTS || localAttempts >= MAX_ATTEMPTS) return

      lastStart = now
      localAttempts++
      countAttempt()

      frameRef.current?.contentWindow?.postMessage(
        { externalCommand: 'call-custom-oauth-login', service },
        origin,
      )

      /* اگر فرمان بی‌اثر ماند، یک بارِ دیگر — سقفِ تلاش‌ها همچنان حلقه را می‌بندد. */
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined
        if (verified || leaving) return
        lastStart = 0
        startLogin()
      }, LOGIN_RETRY_MS)
    }

    /*
      ==================================================================
      🔴 فرمانِ ورود فقط به Rocket.Chat ِ «آماده» فرستاده می‌شود.

      ⚠️ دیده‌شده ۲۰۲۶-۱۰-۰۵ در لاگِ nginx: پنل فرمان را فرستاد
      (`pf-chat-sso-attempts` = 1) ولی هیچ درخواستی به
      `/oauth/rocketchat/authorize` نرسید. اسکریپتِ «خارج‌شده» زودتر از
      آماده‌شدنِ خودِ Rocket.Chat اجرا می‌شود و فرمانی که آن لحظه برسد
      بی‌صدا دور ریخته می‌شود — و قفلِ ۱۵ثانیه‌ایِ `startLogin` تلاشِ
      بعدی را هم می‌گرفت.

      پس `logged-out` فقط «ورود لازم است» را ثبت می‌کند و فرمان با
      رویدادِ `startup` (که Rocket.Chat پس از آماده‌شدن می‌فرستد) می‌رود.
      اگر `startup` نیامد، پس از چند ثانیه به‌هرحال فرستاده می‌شود.
      ==================================================================
    */
    let rcReady = false
    let loginWanted = false
    let readyFallback: number | undefined
    let retryTimer: number | undefined

    const requestLogin = () => {
      if (rcReady) {
        startLogin()
        return
      }
      loginWanted = true
      if (readyFallback !== undefined) window.clearTimeout(readyFallback)
      readyFallback = window.setTimeout(() => {
        readyFallback = undefined
        if (!loginWanted) return
        loginWanted = false
        startLogin()
      }, READY_FALLBACK_MS)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== origin) return

      const data = event.data as { carbon?: string; emails?: unknown; eventName?: string; data?: unknown } | null

      /* ⚠️ عدد و اعلانِ کاربرِ دیگری که هنوز در قاب مانده، نشان داده نمی‌شود. */
      const untrusted = heardFromScripts && !verified

      if (data?.eventName === 'unread-changed') {
        if (untrusted) return
        setChatUnread(data.data)
        return
      }

      /*
        🔴 اعلانِ پیام — Rocket.Chat خودش نمی‌تواند نشانش بدهد (توضیح
        در `chatNotification.ts`).

        ⚠️ کسی که همین حالا گفتگو را جلوی چشم دارد اعلان نمی‌گیرد —
        پیام را در خودِ Rocket.Chat می‌بیند. پنجرهٔ بی‌تمرکز یا تبِ
        پنهان → اعلانِ دسکتاپ؛ پنل جلوی چشم ولی صفحهٔ دیگر → toast.
      */
      if (data?.eventName === 'notification') {
        if (untrusted) return
        const item = parseChatNotification(data.data)
        if (!item) return

        const { visible: onChat, navigate: go, toast: notify } = live.current
        const focused = document.visibilityState === 'visible' && document.hasFocus()
        if (onChat && focused) return

        const openRoom = () => {
          go('/app/chat')
          if (item.path) {
            frameRef.current?.contentWindow?.postMessage({ externalCommand: 'go', path: item.path }, origin)
          }
        }

        if (!focused && showDesktopNotification(item, openRoom)) return
        if (document.visibilityState !== 'visible') return

        notify.toast({
          title: item.title,
          description: item.body || undefined,
          tone: 'info',
          duration: 6000,
          action: { label: 'باز کردنِ گفتگو', onClick: openRoom },
        })
        return
      }

      if (data?.carbon === 'logged-in') {
        heardFromScripts = true
        rcReady = true
        if (retryTimer !== undefined) window.clearTimeout(retryTimer)
        retryTimer = undefined
        postTheme()

        /* اسکریپتِ قدیمی ایمیل نمی‌فرستد — رفتارِ قبلی. */
        if (Array.isArray(data.emails)) {
          const { panelEmail: expected } = live.current
          const emails = data.emails.map((e) => String(e).toLowerCase())

          if (expected === null || !emails.includes(expected)) {
            verified = false
            /* ⚠️ قابِ کاربرِ دیگر هرگز از زیرِ پوشش بیرون نمی‌آید. */
            clearStall()
            setSignedIn(false)
            setChatUnread(0)

            /*
              ⚠️ اگر پس از چند بار خروج و ورود باز هم کسِ دیگری وارد شد
              (مثلاً ایمیلِ حسابِ Rocket.Chat با پنل فرق دارد)، حلقه
              نمی‌سازیم؛ پوشش می‌ماند و پیامِ روشن نشان داده می‌شود.
            */
            if (++mismatches > MAX_ATTEMPTS) {
              setMismatch(true)
              return
            }
            requestSignOut()
            return
          }
        }

        verified = true
        clearStall()
        resetAttempts()
        setMismatch(false)
        setSignedIn(true)
        return
      }

      /*
        🔴 «خارج شد» از دو پیامِ جدا می‌آید.

        `signed-out` تأییدِ فرمانِ خروج است و قاب بلافاصله پس از آن
        بارگذاریِ دوباره می‌کند — پس اینجا ورود شروع **نمی‌شود**: فرمانی
        که به صفحهٔ در حالِ رفتن برسد گم می‌شود و تا ۱۵ ثانیه جلوی
        تلاشِ بعدی را هم می‌گیرد. `logged-out` را صفحهٔ تازه می‌فرستد
        و ورود از آنجا شروع می‌شود.
      */
      if (data?.carbon === 'signed-out' || data?.carbon === 'logged-out') {
        heardFromScripts = true
        verified = false
        signedOut?.()
        signedOut = null
        setSignedIn(false)
        setChatUnread(0)
        if (data.carbon === 'logged-out') {
          postTheme()
          if (!leaving) {
            requestLogin()
            armStall()
          }
        } else {
          /* قاب بارگذاریِ دوباره می‌کند — تا `startup` ِ صفحهٔ تازه، آماده نیست. */
          rcReady = false
          clearStall()
        }
        return
      }

      if (data?.eventName === 'startup') {
        rcReady = true
        if (loginWanted) {
          loginWanted = false
          if (readyFallback !== undefined) window.clearTimeout(readyFallback)
          readyFallback = undefined
          if (!leaving) startLogin()
          return
        }
      }

      /* بی‌اسکریپت: همان رفتارِ قبلی — یک بار در هر زبانه. */
      if (data?.eventName === 'startup' && !heardFromScripts && attempts() === 0) startLogin()
    }

    const frame = frameRef.current
    let loadTimer: number | undefined
    const onLoad = () => {
      loadTimer = window.setTimeout(() => {
        if (!heardFromScripts && attempts() === 0) startLogin()
      }, 1500)
    }

    const overlayTimer = window.setTimeout(() => {
      if (!heardFromScripts) setSignedIn(true)
    }, OVERLAY_FALLBACK_MS)

    window.addEventListener('message', onMessage)
    frame?.addEventListener('load', onLoad)

    return () => {
      unregister()
      if (stallTimer !== undefined) window.clearTimeout(stallTimer)
      if (readyFallback !== undefined) window.clearTimeout(readyFallback)
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
      themeObserver.disconnect()
      window.removeEventListener('message', onMessage)
      frame?.removeEventListener('load', onLoad)
      window.clearTimeout(overlayTimer)
      if (loadTimer !== undefined) window.clearTimeout(loadTimer)
    }
  }, [url, service])

  let body
  if (query.isPending) {
    body = <LoadingState label="در حال اتصال به گفتگو…" />
  } else if (query.isError) {
    body = <ApiErrorState error={query.error} title="گفتگو باز نشد" onRetry={() => void query.refetch()} />
  } else if (!url) {
    body = (
      <EmptyState
        title="گفتگو راه‌اندازی نشده"
        description="سرور گفتگو هنوز به این سامانه وصل نشده است — به مدیر سیستم اطلاع دهید."
      />
    )
  } else {
    /*
      🔴 تمامِ ظرفِ محتوا — بی‌عنوان، بی‌حاشیه.

      ⚠️ حاشیهٔ منفی دقیقاً همان padding ِ ظرف در `AppShell` را خنثی
      می‌کند (`p-4` در موبایل، `--pf-content-pad` از `sm`)؛ اگر آن
      عوض شد، این هم باید عوض شود. گوشه‌های گِرد را خودِ ظرف با
      `overflow-hidden` می‌بُرد، پس قاب حاشیه و گوشهٔ خودش را ندارد.
      ۲px برای حاشیهٔ بالا و پایینِ ظرف کم می‌شود تا صفحه اسکرول نخورد.
    */
    body = (
      <div className="relative -m-4 h-[calc(100dvh-var(--pf-topbar-h)-2px)] min-h-[28rem] sm:-m-[var(--pf-content-pad)]">
        <iframe
          ref={frameRef}
          src={`${url}/home`}
          title="گفتگو"
          className="absolute inset-0 h-full w-full border-0 bg-[var(--pf-surface)]"
          allow="clipboard-write; microphone; camera; display-capture; fullscreen"
        />

        {!signedIn && !loginStalled && (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--pf-surface)]">
            {mismatch ? (
              <EmptyState
                title="حساب گفتگو با حساب شما یکی نیست"
                description="گفتگو با حساب دیگری باز می‌شود. به مدیر سیستم اطلاع دهید که ایمیل حساب گفتگوی شما را بررسی کند."
              />
            ) : (
              <LoadingState label="در حال ورود به گفتگو…" />
            )}
          </div>
        )}
      </div>
    )
  }

  return <div hidden={!visible}>{body}</div>
}
